/**
 * Embedded Weapon List - Compact weapon selection for PowerManagementPanel
 * 
 * Handles weapons, turrets, and missiles with component swapping:
 * - Weapons: Hardpoint → Gimbal/Mount → Weapon
 * - Turrets: Hardpoint (TurretBase) → Turret Controller → Weapons
 * - Missiles: Hardpoint → Missile Rack/Launcher → Missiles
 */

import React, { useState, useMemo, useCallback } from 'react';
import type { Ship, ShipHardpoint } from '../../services/shipService';
import { componentService, ComponentItem } from '../../services/componentService';
import { ChevronDown, Check, Lock, Circle, Crosshair, Rocket, RefreshCw } from 'lucide-react';
import styles from './EmbeddedWeaponList.module.css';

interface EmbeddedWeaponListProps {
  ship: Ship;
  hardpoints: ShipHardpoint[];
  readOnly?: boolean;
  onComponentChange?: (hardpointId: number, newItemId: number | null) => void;
  onLoadedItemChange?: (hardpointId: number, loadedItemId: number, newItemId: number | null) => void;
  hardpointOverrides?: Map<number, ComponentItem>;
  loadedItemOverrides?: Map<string, ComponentItem>;
  onResetWeapons?: () => void;
}

interface WeaponSlotInfo {
  slotType: 'weapon';
  hardpointId: number;
  hardpointSize: number;
  currentMountType: 'Fixed' | 'Gimballed';
  stockMountType: 'Fixed' | 'Gimballed';
  effectiveWeaponSize: number;
  slotName: string;
  isBespoke: boolean;
  isFixedOnly: boolean;
  weaponName: string;
  displayName: string;
  manufacturer: string;
  damageType: 'ballistic' | 'energy' | 'distortion' | 'unknown';
  dps: number;
  fireRate: number | null;
  damagePerShot: number | null;
  hasOverride: boolean;
}

interface TurretSlotInfo {
  slotType: 'turret';
  hardpointId: number;
  hardpointSize: number;
  slotName: string;
  turretName: string;
  turretDisplayName: string;
  weaponCount: number;
  weapons: {
    loadedItemId: number;
    weaponName: string;
    displayName: string;
    size: number;
    manufacturer: string;
    damageType: 'ballistic' | 'energy' | 'distortion' | 'unknown';
    dps: number;
    fireRate: number;
    damagePerShot: number;
    range: number;
    velocity: number;
    ammoCapacity: number;
    grade: number;
  }[];
  totalDPS: number;
  hasOverride: boolean;
}

interface MissileSlotInfo {
  slotType: 'missile';
  hardpointId: number;
  hardpointSize: number;
  slotName: string;
  rackName: string;
  rackDisplayName: string;
  missileCount: number;
  missileSize: number;
  missileName: string;
  missileDisplayName: string;
  missileType: 'Missile' | 'Torpedo' | 'Bomb' | 'Unknown';
  totalDamage: number;
  hasOverride: boolean;
  loadedItemId?: number; // First missile's loadedItem ID for swapping
  // Missile stats
  damage: number;
  speed: number;
  lockTime: number;
  launchDelay: number;
  lockingRange: number;
  manufacturer: string;
  missileGrade: number;
}

const calculateDPS = (fireRate: number | null, damagePerShot: number | null): number => {
  if (!fireRate || !damagePerShot) return 0;
  return (damagePerShot * fireRate) / 60;
};

const getDamageType = (damageType: string | null | undefined, itemName: string): 'ballistic' | 'energy' | 'distortion' | 'unknown' => {
  const type = (damageType || '').toLowerCase();
  if (type.includes('ballistic') || type.includes('physical')) return 'ballistic';
  if (type.includes('energy') || type.includes('laser')) return 'energy';
  if (type.includes('distortion') || type.includes('emp')) return 'distortion';
  
  const name = itemName.toLowerCase();
  if (name.includes('repeater') || name.includes('cannon') || name.includes('gatling')) return 'ballistic';
  if (name.includes('laser') || name.includes('plasma') || name.includes('beam')) return 'energy';
  if (name.includes('distortion') || name.includes('suckerpunch')) return 'distortion';
  
  return 'unknown';
};

const isBespokeWeapon = (tags: string | null | undefined, requiredTags: string | null | undefined): boolean => {
  const t = (tags || '').toLowerCase();
  const rt = (requiredTags || '').toLowerCase();
  return t.includes('bespoke') || rt.length > 0;
};

const damageTypeColors: Record<string, string> = {
  ballistic: '#ffa500',
  energy: '#00bfff',
  distortion: '#9966ff',
  unknown: '#888888'
};

export const EmbeddedWeaponList: React.FC<EmbeddedWeaponListProps> = ({
  ship,
  hardpoints,
  readOnly = false,
  onComponentChange,
  onLoadedItemChange,
  hardpointOverrides,
  loadedItemOverrides,
  onResetWeapons
}) => {
  console.log('=== EMBEDDED WEAPON LIST RENDER ===');
  console.log('Hardpoints received:', hardpoints?.length || 0);
  console.log('Ship:', ship?.name);
  
  const [expandedSlot, setExpandedSlot] = useState<number | null>(null);
  const [expandedTurretWeapon, setExpandedTurretWeapon] = useState<string | null>(null); // Format: "hardpointId-loadedItemId"
  const [expandedMissile, setExpandedMissile] = useState<number | null>(null);
  const [availableWeapons, setAvailableWeapons] = useState<Map<number, ComponentItem[]>>(new Map());
  const [availableMissiles, setAvailableMissiles] = useState<Map<number, ComponentItem[]>>(new Map());
  const [loadingWeapons, setLoadingWeapons] = useState<Set<number>>(new Set());
  const [loadingMissiles, setLoadingMissiles] = useState<Set<number>>(new Set());
  const [mountOverrides, setMountOverrides] = useState<Map<number, 'Fixed' | 'Gimballed'>>(new Map());

  const isInternalItem = (itemName: string, itemType: string, subType: string): boolean => {
    const name = itemName.toLowerCase();
    const type = itemType.toLowerCase();
    return name.includes('controller') || name.includes('turretbase') || name.includes('placeholder') ||
           type === 'turretbase' || type === 'turret' || type === 'weaponattachment';
  };

  const isActualWeapon = (itemDef: any): boolean => {
    if (!itemDef?.weaponStats) return false;
    const type = (itemDef.type || '').toLowerCase();
    return type === 'weapon' || type === 'weapongun';
  };

  const findWeaponInLoadedItems = (loadedItems: any[]): any | null => {
    for (const item of loadedItems) {
      const itemDef = item.itemDefinition;
      const itemName = item.itemName || '';
      const itemType = itemDef?.type || '';
      const subType = itemDef?.subType || '';
      
      if (!isInternalItem(itemName, itemType, subType) && isActualWeapon(itemDef)) {
        return { item, itemDef };
      }
      if (item.childAttachments?.length > 0) {
        const found = findWeaponInLoadedItems(item.childAttachments);
        if (found) return found;
      }
    }
    return null;
  };

  const weaponSlots = useMemo((): WeaponSlotInfo[] => {
    const slots: WeaponSlotInfo[] = [];
    
    hardpoints.forEach(hp => {
      if (hp.hardpointType !== 'Weapon') return;
      
      const slotName = (hp.slotName || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      if (slotName.includes('controller') || defaultName.includes('controller')) return;
      
      const override = hardpointOverrides?.get(hp.id);
      const mountOverride = mountOverrides.get(hp.id);
      const stockMountType = (hp.mountType === 'Gimballed' ? 'Gimballed' : 'Fixed') as 'Fixed' | 'Gimballed';
      const currentMountType = mountOverride || stockMountType;
      
      let isBespoke = false;
      let isFixedOnly = stockMountType === 'Fixed';
      let weaponItemDef: any = null;
      let weaponItemName = '';
      
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        const firstItem = hp.loadedItems[0];
        const firstItemType = (firstItem.itemDefinition?.type || '').toLowerCase();
        const isWeaponPart = firstItemType === 'weaponattachment';
        
        if (isWeaponPart && hp.itemDefinition?.weaponStats) {
          weaponItemDef = hp.itemDefinition;
          weaponItemName = hp.defaultItemName || '';
          isBespoke = true;
          isFixedOnly = true;
        } else {
          const found = findWeaponInLoadedItems(hp.loadedItems);
          if (found) {
            weaponItemDef = found.itemDef;
            weaponItemName = found.item.itemName || '';
            isBespoke = isBespokeWeapon(weaponItemDef?.tags, weaponItemDef?.requiredTags);
          }
        }
      } else if (hp.itemDefinition?.weaponStats) {
        weaponItemDef = hp.itemDefinition;
        weaponItemName = hp.defaultItemName || '';
        isBespoke = isBespokeWeapon(weaponItemDef?.tags, weaponItemDef?.requiredTags);
      }
      
      const effectiveItemDef = override || weaponItemDef;
      if (!effectiveItemDef && !isBespoke) return;
      
      const weaponStats = effectiveItemDef?.weaponStats;
      const displayName = effectiveItemDef?.localizedName || hp.defaultItemDisplayName || 'Unknown';
      
      if (displayName.toLowerCase().includes('placeholder')) return;
      
      const hardpointSize = hp.size || effectiveItemDef?.size || 0;
      const effectiveWeaponSize = currentMountType === 'Gimballed' ? Math.max(1, hardpointSize - 1) : hardpointSize;
      
      slots.push({
        slotType: 'weapon',
        hardpointId: hp.id,
        hardpointSize,
        currentMountType,
        stockMountType,
        effectiveWeaponSize,
        slotName: hp.slotName || '',
        isBespoke,
        isFixedOnly,
        weaponName: override?.itemName || weaponItemName,
        displayName,
        manufacturer: effectiveItemDef?.manufacturer || '',
        damageType: getDamageType(weaponStats?.damageType, weaponItemName),
        dps: calculateDPS(weaponStats?.fireRate || null, weaponStats?.damagePerShot || null),
        fireRate: weaponStats?.fireRate || null,
        damagePerShot: weaponStats?.damagePerShot || null,
        hasOverride: !!override || !!mountOverride
      });
    });
    
    return slots.sort((a, b) => {
      if (b.hardpointSize !== a.hardpointSize) return b.hardpointSize - a.hardpointSize;
      return a.slotName.localeCompare(b.slotName);
    });
  }, [hardpoints, hardpointOverrides, mountOverrides]);

  // Parse turret hardpoints
  const turretSlots = useMemo((): TurretSlotInfo[] => {
    const slots: TurretSlotInfo[] = [];
    
    console.log('=== TURRET PARSING ===');
    console.log('Total hardpoints:', hardpoints.length);
    
    hardpoints.forEach(hp => {
      // Log detailed info for potential turret hardpoints
      if (hp.slotName?.includes('turret') && !hp.slotName.includes('access')) {
        console.log(`\n=== TURRET HARDPOINT: ${hp.slotName} ===`);
        console.log('Hardpoint type:', hp.hardpointType);
        console.log('Default item name:', hp.defaultItemName);
        console.log('LoadedItems count:', hp.loadedItems?.length || 0);
        console.log('Has itemDefinition:', !!hp.itemDefinition);
        
        if (hp.itemDefinition) {
          const itemDef = hp.itemDefinition as any;
          console.log('ItemDefinition:', {
            type: hp.itemDefinition.type,
            subType: hp.itemDefinition.subType,
            localizedName: hp.itemDefinition.localizedName,
            hasLoadout: !!itemDef.loadout,
            loadoutSize: itemDef.loadout?.length || 0
          });
          
          if (itemDef.loadout && itemDef.loadout.length > 0) {
            console.log('Loadout entries:');
            itemDef.loadout.forEach((entry: any, idx: number) => {
              console.log(`  Loadout ${idx}:`, {
                itemName: entry.itemName,
                type: entry.itemDefinition?.type,
                hasWeaponStats: !!entry.itemDefinition?.weaponStats
              });
            });
          }
        }
        
        if (hp.loadedItems && hp.loadedItems.length > 0) {
          hp.loadedItems.forEach((item, idx) => {
            console.log(`  LoadedItem ${idx}:`, {
              itemName: item.itemName,
              type: item.itemDefinition?.type,
              childAttachments: item.childAttachments?.length || 0
            });
          });
        }
      }
      
      // Turrets can be hardpointType 'Turret', 'TurretBase', or 'MannedTurret'
      if (hp.hardpointType !== 'Turret' && hp.hardpointType !== 'TurretBase' && hp.hardpointType !== 'MannedTurret') return;
      
      const override = hardpointOverrides?.get(hp.id);
      const turretName = override?.itemName || hp.defaultItemName || '';
      const turretDisplayName = override?.localizedName || hp.defaultItemDisplayName || hp.itemDefinition?.localizedName || 'Unknown Turret';
      
      // Extract weapons from loadedItems
      const weapons: TurretSlotInfo['weapons'] = [];
      let totalDPS = 0;
      
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        // Build a Set of all weapon IDs that appear in childAttachments to prevent duplicates
        // The backend flattens the hierarchy: a weapon appears BOTH as a direct loadedItem 
        // AND as a childAttachment of its mount/gimbal - we need to deduplicate
        const childWeaponIds = new Set<number>();
        hp.loadedItems.forEach(loadedItem => {
          if (loadedItem.childAttachments && loadedItem.childAttachments.length > 0) {
            loadedItem.childAttachments.forEach((child: any) => {
              if (child.id) {
                childWeaponIds.add(child.id);
              }
            });
          }
        });
        
        hp.loadedItems.forEach(loadedItem => {
          // Skip internal controllers
          const itemName = (loadedItem.itemName || '').toLowerCase();
          if (itemName.includes('controller') || itemName.includes('turretbase')) return;
          
          // Check if this loaded item is a weapon
          const itemDef = loadedItem.itemDefinition;
          if (itemDef && isActualWeapon(itemDef)) {
            // Skip if this weapon is already in childAttachments (duplicate from backend)
            if (loadedItem.id && childWeaponIds.has(loadedItem.id)) {
              return; // Skip - will be processed when we encounter its parent mount
            }
            
            // Check if this weapon has been overridden
            const overrideKey = `${hp.id}:${loadedItem.id}`;
            const overrideItem = loadedItemOverrides?.get(overrideKey);
            const effectiveItemDef = overrideItem || itemDef;
            const effectiveItemName = overrideItem?.itemName || loadedItem.itemName || '';
            
            const weaponStats = effectiveItemDef.weaponStats;
            const dps = calculateDPS(weaponStats?.fireRate || null, weaponStats?.damagePerShot || null);
            totalDPS += dps;
            
            weapons.push({
              loadedItemId: loadedItem.id,
              weaponName: effectiveItemName,
              displayName: effectiveItemDef.localizedName || loadedItem.displayName || 'Unknown',
              size: effectiveItemDef.size || loadedItem.size || 0,
              manufacturer: effectiveItemDef.manufacturer || '',
              damageType: getDamageType(weaponStats?.damageType, effectiveItemName),
              dps,
              fireRate: weaponStats?.fireRate || 0,
              damagePerShot: weaponStats?.damagePerShot || 0,
              range: weaponStats?.range || 0,
              velocity: weaponStats?.velocity || 0,
              ammoCapacity: weaponStats?.ammoCapacity || 0,
              grade: effectiveItemDef.grade || 3
            });
          } else if (loadedItem.childAttachments && loadedItem.childAttachments.length > 0) {
            // This loadedItem is a mount/gimbal - check its children
            loadedItem.childAttachments.forEach((child: any) => {
              const childItemDef = child.itemDefinition;
              if (childItemDef && isActualWeapon(childItemDef)) {
                // Check if this weapon has been overridden
                const overrideKey = `${hp.id}:${child.id}`;
                const overrideItem = loadedItemOverrides?.get(overrideKey);
                const effectiveItemDef = overrideItem || childItemDef;
                const effectiveItemName = overrideItem?.itemName || child.itemName || '';
                
                const weaponStats = effectiveItemDef.weaponStats;
                const dps = calculateDPS(weaponStats?.fireRate || null, weaponStats?.damagePerShot || null);
                totalDPS += dps;
                
                weapons.push({
                  loadedItemId: child.id,
                  weaponName: effectiveItemName,
                  displayName: effectiveItemDef.localizedName || child.displayName || 'Unknown',
                  size: effectiveItemDef.size || child.size || 0,
                  manufacturer: effectiveItemDef.manufacturer || '',
                  damageType: getDamageType(weaponStats?.damageType, effectiveItemName),
                  dps,
                  fireRate: weaponStats?.fireRate || 0,
                  damagePerShot: weaponStats?.damagePerShot || 0,
                  range: weaponStats?.range || 0,
                  velocity: weaponStats?.velocity || 0,
                  ammoCapacity: weaponStats?.ammoCapacity || 0,
                  grade: effectiveItemDef.grade || 3
                });
              }
            });
          }
        });
      }
      
      // Only add turret if it has weapons
      if (weapons.length > 0) {
        slots.push({
          slotType: 'turret',
          hardpointId: hp.id,
          hardpointSize: hp.size || 0,
          slotName: hp.slotName || '',
          turretName,
          turretDisplayName,
          weaponCount: weapons.length,
          weapons,
          totalDPS,
          hasOverride: !!override
        });
      }
    });
    
    return slots.sort((a, b) => {
      if (b.hardpointSize !== a.hardpointSize) return b.hardpointSize - a.hardpointSize;
      return a.slotName.localeCompare(b.slotName);
    });
  }, [hardpoints, hardpointOverrides, loadedItemOverrides]);

  // Parse missile hardpoints
  const missileSlots = useMemo((): MissileSlotInfo[] => {
    const slots: MissileSlotInfo[] = [];
    
    hardpoints.forEach(hp => {
      // Missiles have hardpointType 'MissileRack' or componentType 'MissileLauncher'
      if (hp.hardpointType !== 'MissileRack' && hp.componentType !== 'MissileLauncher') return;
      
      const override = hardpointOverrides?.get(hp.id);
      const rackName = override?.itemName || hp.defaultItemName || '';
      const rackDisplayName = override?.localizedName || hp.defaultItemDisplayName || hp.itemDefinition?.localizedName || 'Unknown Rack';
      
      // Extract missiles from loadedItems
      let missileCount = 0;
      let missileSize = 0;
      let missileName = '';
      let missileDisplayName = '';
      let missileType: 'Missile' | 'Torpedo' | 'Bomb' | 'Unknown' = 'Unknown';
      let totalDamage = 0;
      let firstMissileLoadedItemId: number | undefined = undefined;
      let rackOverride: ComponentItem | undefined = undefined; // Override applies to ALL missiles in rack
      
      // First pass: check if the first missile has an override (applies to all missiles in rack)
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        for (const loadedItem of hp.loadedItems) {
          const itemDef = loadedItem.itemDefinition;
          const itemType = (itemDef?.type || '').toLowerCase();
          
          if (itemDef && (itemType.includes('missile') || itemType.includes('torpedo') || itemType.includes('bomb'))) {
            firstMissileLoadedItemId = loadedItem.id;
            const overrideKey = `${hp.id}:${loadedItem.id}`;
            rackOverride = loadedItemOverrides?.get(overrideKey);
            break; // Found first missile, check for override
          }
          
          // Check children
          if (loadedItem.childAttachments && loadedItem.childAttachments.length > 0) {
            for (const child of loadedItem.childAttachments) {
              const childItemDef = child.itemDefinition;
              const childItemType = (childItemDef?.type || '').toLowerCase();
              
              if (childItemDef && (childItemType.includes('missile') || childItemType.includes('torpedo') || childItemType.includes('bomb'))) {
                firstMissileLoadedItemId = child.id;
                const overrideKey = `${hp.id}:${child.id}`;
                rackOverride = loadedItemOverrides?.get(overrideKey);
                break; // Found first missile, check for override
              }
            }
            if (firstMissileLoadedItemId) break;
          }
        }
      }
      
      // Second pass: count missiles and calculate stats (using rack override if present)
      let missileSpeed = 0;
      let missileLockTime = 0;
      let missileLaunchDelay = 0;
      let missileLockingRange = 0;
      let missileManufacturer = '';
      let missileGrade = 3;
      let missileDamage = 0;
      
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        hp.loadedItems.forEach(loadedItem => {
          const itemDef = loadedItem.itemDefinition;
          const itemType = (itemDef?.type || '').toLowerCase();
          
          // Check if this is a missile/torpedo/bomb
          if (itemDef && (itemType.includes('missile') || itemType.includes('torpedo') || itemType.includes('bomb'))) {
            // Use rack override if present, otherwise use original
            const effectiveItemDef = rackOverride || itemDef;
            const effectiveItemName = rackOverride?.itemName || loadedItem.itemName || '';
            const effectiveItemType = (effectiveItemDef?.type || '').toLowerCase();
            
            // Capture display info from the first missile
            if (missileCount === 0) {
              missileSize = effectiveItemDef.size || loadedItem.size || 0;
              missileName = effectiveItemName;
              missileDisplayName = effectiveItemDef.localizedName || loadedItem.displayName || 'Unknown';
              missileManufacturer = effectiveItemDef.manufacturer || 'Unknown';
              missileGrade = effectiveItemDef.grade || 3;
              missileDamage = effectiveItemDef.missileStats?.damage || 0;
              missileSpeed = effectiveItemDef.missileStats?.speed || 0;
              missileLockTime = effectiveItemDef.missileStats?.lockTime || 0;
              missileLaunchDelay = effectiveItemDef.missileStats?.launchDelay || 0;
              missileLockingRange = effectiveItemDef.missileStats?.lockingRange || 0;
              
              // Determine missile type
              if (effectiveItemType.includes('torpedo')) missileType = 'Torpedo';
              else if (effectiveItemType.includes('bomb')) missileType = 'Bomb';
              else if (effectiveItemType.includes('missile')) missileType = 'Missile';
            }
            
            // Count all missiles and sum damage
            missileCount++;
            const damage = effectiveItemDef.missileStats?.damage || 0;
            totalDamage += damage;
          }
          
          // Check childAttachments for missiles (racks may have nested structure)
          if (loadedItem.childAttachments && loadedItem.childAttachments.length > 0) {
            loadedItem.childAttachments.forEach((child: any) => {
              const childItemDef = child.itemDefinition;
              const childItemType = (childItemDef?.type || '').toLowerCase();
              
              if (childItemDef && (childItemType.includes('missile') || childItemType.includes('torpedo') || childItemType.includes('bomb'))) {
                // Use rack override if present, otherwise use original
                const effectiveItemDef = rackOverride || childItemDef;
                const effectiveItemName = rackOverride?.itemName || child.itemName || '';
                const effectiveItemType = (effectiveItemDef?.type || '').toLowerCase();
                
                // Capture display info from the first missile
                if (missileCount === 0) {
                  missileSize = effectiveItemDef.size || child.size || 0;
                  missileName = effectiveItemName;
                  missileDisplayName = effectiveItemDef.localizedName || child.displayName || 'Unknown';
                  
                  if (effectiveItemType.includes('torpedo')) missileType = 'Torpedo';
                  else if (effectiveItemType.includes('bomb')) missileType = 'Bomb';
                  else if (effectiveItemType.includes('missile')) missileType = 'Missile';
                }
                
                // Count all missiles and sum damage
                missileCount++;
                const damage = effectiveItemDef.missileStats?.damage || 0;
                totalDamage += damage;
              }
            });
          }
        });
      }
      
      // Only add missile slot if it has missiles loaded
      if (missileCount > 0) {
        slots.push({
          slotType: 'missile',
          hardpointId: hp.id,
          hardpointSize: hp.size || 0,
          slotName: hp.slotName || '',
          rackName,
          rackDisplayName,
          missileCount,
          missileSize,
          missileName,
          missileDisplayName,
          missileType,
          totalDamage,
          hasOverride: !!override,
          loadedItemId: firstMissileLoadedItemId,
          // Missile stats
          damage: missileDamage,
          speed: missileSpeed,
          lockTime: missileLockTime,
          launchDelay: missileLaunchDelay,
          lockingRange: missileLockingRange,
          manufacturer: missileManufacturer,
          missileGrade: missileGrade
        });
      }
    });
    
    return slots.sort((a, b) => {
      if (b.hardpointSize !== a.hardpointSize) return b.hardpointSize - a.hardpointSize;
      return a.slotName.localeCompare(b.slotName);
    });
  }, [hardpoints, hardpointOverrides, loadedItemOverrides]);

  const totals = useMemo(() => {
    let totalDPS = 0;
    let ballisticDPS = 0;
    let energyDPS = 0;
    let distortionDPS = 0;
    
    // Add weapon slots DPS
    weaponSlots.forEach(w => {
      if (w.dps > 0) {
        totalDPS += w.dps;
        if (w.damageType === 'ballistic') ballisticDPS += w.dps;
        else if (w.damageType === 'energy') energyDPS += w.dps;
        else if (w.damageType === 'distortion') distortionDPS += w.dps;
      }
    });
    
    // Add turret DPS
    turretSlots.forEach(t => {
      if (t.totalDPS > 0) {
        totalDPS += t.totalDPS;
        // Add each turret weapon's DPS by damage type
        t.weapons.forEach(w => {
          if (w.dps > 0) {
            if (w.damageType === 'ballistic') ballisticDPS += w.dps;
            else if (w.damageType === 'energy') energyDPS += w.dps;
            else if (w.damageType === 'distortion') distortionDPS += w.dps;
          }
        });
      }
    });
    
    return { totalDPS, ballisticDPS, energyDPS, distortionDPS, count: weaponSlots.length + turretSlots.length };
  }, [weaponSlots, turretSlots]);

  const loadWeaponsForSize = useCallback(async (size: number) => {
    if (availableWeapons.has(size) || loadingWeapons.has(size)) return;
    
    setLoadingWeapons(prev => new Set(prev).add(size));
    try {
      const response = await componentService.getComponentsByType('WeaponGun', {
        size,
        pageSize: 100,
        sortBy: 'name'
      });
      const filtered = response.items.filter(w => {
        if (w.requiredTags && w.requiredTags.trim() !== '') return false;
        const name = (w.localizedName || w.itemName || '').toLowerCase();
        if (name.includes('placeholder')) return false;
        const dps = ((w.weaponStats?.damagePerShot || 0) * (w.weaponStats?.fireRate || 0)) / 60;
        if (dps <= 0) return false;
        return true;
      });
      setAvailableWeapons(prev => new Map(prev).set(size, filtered));
    } catch (err) {
      console.error(`Failed to load S${size} weapons:`, err);
    } finally {
      setLoadingWeapons(prev => {
        const next = new Set(prev);
        next.delete(size);
        return next;
      });
    }
  }, [availableWeapons, loadingWeapons]);

  const handleMountChange = useCallback((hardpointId: number, newMount: 'Fixed' | 'Gimballed') => {
    setMountOverrides(prev => {
      const next = new Map(prev);
      next.set(hardpointId, newMount);
      return next;
    });
    if (onComponentChange) {
      onComponentChange(hardpointId, null);
    }
  }, [onComponentChange]);

  const handleWeaponSelect = useCallback((hardpointId: number, weapon: ComponentItem | null) => {
    console.log('🔫 handleWeaponSelect called:', { hardpointId, weaponId: weapon?.id, weaponName: weapon?.localizedName });
    if (onComponentChange) {
      onComponentChange(hardpointId, weapon?.id || null);
    } else {
      console.warn('⚠️ onComponentChange callback is not defined');
    }
    setExpandedSlot(null);
  }, [onComponentChange]);

  const toggleDropdown = useCallback((hardpointId: number, effectiveSize: number) => {
    if (readOnly) return;
    if (expandedSlot === hardpointId) {
      setExpandedSlot(null);
    } else {
      setExpandedSlot(hardpointId);
      loadWeaponsForSize(effectiveSize);
    }
  }, [readOnly, expandedSlot, loadWeaponsForSize]);

  const toggleTurretWeaponDropdown = useCallback((hardpointId: number, loadedItemId: number, weaponSize: number) => {
    if (readOnly) return;
    const key = `${hardpointId}-${loadedItemId}`;
    if (expandedTurretWeapon === key) {
      setExpandedTurretWeapon(null);
    } else {
      setExpandedTurretWeapon(key);
      loadWeaponsForSize(weaponSize);
    }
  }, [readOnly, expandedTurretWeapon, loadWeaponsForSize]);

  const handleTurretWeaponSelect = useCallback((hardpointId: number, loadedItemId: number, weapon: ComponentItem | null) => {
    console.log('🎯 handleTurretWeaponSelect called:', { hardpointId, loadedItemId, weaponId: weapon?.id, weaponName: weapon?.localizedName });
    if (onLoadedItemChange) {
      onLoadedItemChange(hardpointId, loadedItemId, weapon?.id || null);
    } else {
      console.warn('⚠️ onLoadedItemChange callback is not defined');
    }
    setExpandedTurretWeapon(null);
  }, [onLoadedItemChange]);

  const loadMissilesForSize = useCallback(async (size: number) => {
    if (availableMissiles.has(size) || loadingMissiles.has(size)) return;
    
    setLoadingMissiles(prev => new Set(prev).add(size));
    try {
      const response = await componentService.getComponentsByType('Missile', {
        size,
        pageSize: 100,
        sortBy: 'name'
      });
      const filtered = response.items.filter(m => {
        if (m.requiredTags && m.requiredTags.trim() !== '') return false;
        const name = (m.localizedName || m.itemName || '').toLowerCase();
        if (name.includes('placeholder')) return false;
        return true;
      });
      setAvailableMissiles(prev => new Map(prev).set(size, filtered));
    } catch (err) {
      console.error(`Failed to load S${size} missiles:`, err);
    } finally {
      setLoadingMissiles(prev => {
        const next = new Set(prev);
        next.delete(size);
        return next;
      });
    }
  }, [availableMissiles, loadingMissiles]);

  const toggleMissileDropdown = useCallback((hardpointId: number, missileSize: number) => {
    if (readOnly) return;
    if (expandedMissile === hardpointId) {
      setExpandedMissile(null);
    } else {
      setExpandedMissile(hardpointId);
      loadMissilesForSize(missileSize);
    }
  }, [readOnly, expandedMissile, loadMissilesForSize]);

  const handleMissileSelect = useCallback((hardpointId: number, loadedItemId: number, missile: ComponentItem | null) => {
    console.log('🚀 handleMissileSelect called:', { hardpointId, loadedItemId, missileId: missile?.id, missileName: missile?.localizedName });
    if (onLoadedItemChange) {
      onLoadedItemChange(hardpointId, loadedItemId, missile?.id || null);
    } else {
      console.warn('⚠️ onLoadedItemChange callback is not defined');
    }
    setExpandedMissile(null);
  }, [onLoadedItemChange]);

  const formatNumber = (num: number | null, decimals: number = 0): string => {
    if (num === null || num === undefined) return '?';
    // Format with commas for thousands
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  // Only show "no weapons" if there are NO weapons, turrets, OR missiles
  if (weaponSlots.length === 0 && turretSlots.length === 0 && missileSlots.length === 0) {
    return <div className={styles.empty}>No weapons, turrets, or missiles found</div>;
  }

  const hasWeaponChanges = (hardpointOverrides && hardpointOverrides.size > 0) || (loadedItemOverrides && loadedItemOverrides.size > 0);

  return (
    <div className={styles.container}>
      {/* DPS Summary */}
      <div className={styles.dpsSummary}>
        <div className={styles.totalDPS}>
          <span className={styles.dpsLabel}>Total DPS</span>
          <span className={styles.dpsValue}>{formatNumber(totals.totalDPS, 1)}</span>
        </div>
        {!readOnly && hasWeaponChanges && onResetWeapons && (
          <button className={styles.resetButton} onClick={onResetWeapons} title="Reset all weapons to stock">
            <RefreshCw size={14} />
            <span>Reset</span>
          </button>
        )}
        <div className={styles.dpsBreakdown}>
            {totals.ballisticDPS > 0 && (
              <div className={styles.dpsType} style={{ color: damageTypeColors.ballistic }}>
                <Circle size={8} fill={damageTypeColors.ballistic} />
                <span>{formatNumber(totals.ballisticDPS, 0)}</span>
              </div>
            )}
            {totals.energyDPS > 0 && (
              <div className={styles.dpsType} style={{ color: damageTypeColors.energy }}>
                <Circle size={8} fill={damageTypeColors.energy} />
                <span>{formatNumber(totals.energyDPS, 0)}</span>
              </div>
            )}
            {totals.distortionDPS > 0 && (
              <div className={styles.dpsType} style={{ color: damageTypeColors.distortion }}>
                <Circle size={8} fill={damageTypeColors.distortion} />
                <span>{formatNumber(totals.distortionDPS, 0)}</span>
              </div>
            )}
          </div>
        </div>

      {/* Weapon Slots */}
      {weaponSlots.length > 0 && (
        <div className={styles.weaponList}>
        {weaponSlots.map(slot => {
          const isExpanded = expandedSlot === slot.hardpointId;
          const sizeWeapons = availableWeapons.get(slot.effectiveWeaponSize) || [];
          const isLoading = loadingWeapons.has(slot.effectiveWeaponSize);
          const canSwap = !slot.isBespoke && !readOnly;
          
          return (
            <div key={slot.hardpointId} className={styles.weaponItem}>
              {/* Mount Type Selector */}
              {!slot.isFixedOnly && !readOnly && (
                <div className={styles.mountSelector}>
                  <button
                    className={`${styles.mountButton} ${slot.currentMountType === 'Fixed' ? styles.active : ''}`}
                    onClick={() => handleMountChange(slot.hardpointId, 'Fixed')}
                  >
                    Fixed S{slot.hardpointSize}
                  </button>
                  <button
                    className={`${styles.mountButton} ${slot.currentMountType === 'Gimballed' ? styles.active : ''}`}
                    onClick={() => handleMountChange(slot.hardpointId, 'Gimballed')}
                  >
                    Gimbal S{slot.hardpointSize - 1}
                  </button>
                </div>
              )}
              
              {/* Weapon Header */}
              <div 
                className={`${styles.weaponHeader} ${canSwap ? styles.clickable : ''}`}
                onClick={() => canSwap && toggleDropdown(slot.hardpointId, slot.effectiveWeaponSize)}
              >
                <div className={styles.weaponInfo}>
                  <div className={styles.sizeIndicator}>S{slot.effectiveWeaponSize}</div>
                  <div className={styles.weaponDetails}>
                    <div className={styles.weaponName}>
                      {slot.displayName}
                      {slot.isBespoke && <Lock size={12} className={styles.lockIcon} />}
                    </div>
                    <div className={styles.weaponMeta}>
                      <span className={styles.mountType}>{slot.currentMountType}</span>
                      {slot.manufacturer && <span className={styles.manufacturer}>{slot.manufacturer}</span>}
                    </div>
                  </div>
                </div>
                <div className={styles.weaponStats}>
                  <div className={styles.damageIndicator} style={{ backgroundColor: damageTypeColors[slot.damageType] }} />
                  <div className={styles.statValue}>
                    <span className={styles.statLabel}>DPS</span>
                    <span>{formatNumber(slot.dps, 1)}</span>
                  </div>
                  {canSwap && (
                    <ChevronDown size={16} className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`} />
                  )}
                </div>
              </div>
              {/* Weapon metadata and stats */}
              <div className={styles.turretWeaponMeta} style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginLeft: '24px', marginTop: '4px' }}>
                {slot.fireRate && slot.fireRate > 0 && (
                  <>
                    <span>{formatNumber(slot.fireRate, 0)} rpm</span>
                    <span style={{ margin: '0 6px' }}>•</span>
                  </>
                )}
                {slot.damagePerShot && slot.damagePerShot > 0 && (
                  <>
                    <span>{formatNumber(slot.damagePerShot, 1)} dmg/shot</span>
                  </>
                )}
              </div>

              {/* Weapon Dropdown */}
              {isExpanded && canSwap && (
                <div className={styles.dropdown}>
                  <div className={styles.dropdownHeader}>
                    Select S{slot.effectiveWeaponSize} {slot.currentMountType} Weapon
                  </div>
                  {isLoading ? (
                    <div className={styles.loading}>Loading...</div>
                  ) : sizeWeapons.length === 0 ? (
                    <div className={styles.noOptions}>No weapons available</div>
                  ) : (
                    <div className={styles.optionsList}>
                      {sizeWeapons.map(w => {
                        const wDPS = calculateDPS(w.weaponStats?.fireRate || null, w.weaponStats?.damagePerShot || null);
                        const wDamageType = getDamageType(w.weaponStats?.damageType, w.itemName);
                        const isSelected = slot.weaponName === w.itemName;
                        
                        return (
                          <div
                            key={w.id}
                            className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleWeaponSelect(slot.hardpointId, w);
                            }}
                          >
                            <div className={styles.optionInfo}>
                              <div className={styles.optionName}>{w.localizedName}</div>
                              <div className={styles.optionMeta}>
                                <span>{w.manufacturer}</span>
                                <span style={{ margin: '0 4px' }}>•</span>
                                <span>S{w.size}</span>
                                <span style={{ margin: '0 4px' }}>•</span>
                                <span>Grade {['A', 'B', 'C', 'D'][w.grade - 1] || '?'}</span>
                              </div>
                              <div className={styles.optionMeta} style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                                {w.weaponStats?.fireRate && w.weaponStats.fireRate > 0 && (
                                  <span>{formatNumber(w.weaponStats.fireRate, 0)} rpm</span>
                                )}
                                {w.weaponStats?.damagePerShot && w.weaponStats.damagePerShot > 0 && (
                                  <>
                                    <span style={{ margin: '0 4px' }}>•</span>
                                    <span>{formatNumber(w.weaponStats.damagePerShot, 1)} dmg</span>
                                  </>
                                )}
                                {w.weaponStats?.range && w.weaponStats.range > 100 && (
                                  <>
                                    <span style={{ margin: '0 4px' }}>•</span>
                                    <span>{formatNumber(w.weaponStats.range / 1000, 1)}km</span>
                                  </>
                                )}
                                {w.weaponStats?.velocity && w.weaponStats.velocity > 0 && (
                                  <>
                                    <span style={{ margin: '0 4px' }}>•</span>
                                    <span>{formatNumber(w.weaponStats.velocity, 0)} m/s</span>
                                  </>
                                )}
                                {w.weaponStats?.ammoCapacity && w.weaponStats.ammoCapacity > 0 && (
                                  <>
                                    <span style={{ margin: '0 4px' }}>•</span>
                                    <span>{formatNumber(w.weaponStats.ammoCapacity, 0)} rounds</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className={styles.optionStats}>
                              <div className={styles.damageIndicator} style={{ backgroundColor: damageTypeColors[wDamageType] }} />
                              <span>{formatNumber(wDPS, 1)} DPS</span>
                              {isSelected && <Check size={14} className={styles.checkIcon} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Turret Slots */}
      {turretSlots.length > 0 && (
        <>
          <div className={styles.sectionHeader}>
            <Crosshair size={16} />
            <span>Turrets ({turretSlots.length})</span>
          </div>
          <div className={styles.weaponList}>
            {turretSlots.map(slot => (
              <div key={slot.hardpointId} className={styles.weaponItem}>
                <div className={styles.weaponHeader}>
                  <div className={styles.weaponInfo}>
                    <div className={styles.sizeIndicator}>S{slot.hardpointSize}</div>
                    <div className={styles.weaponDetails}>
                      <div className={styles.weaponName}>{slot.turretDisplayName}</div>
                      <div className={styles.weaponMeta}>
                        <span>{slot.weaponCount} weapon{slot.weaponCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.weaponStats}>
                    <div className={styles.statValue}>
                      <span className={styles.statLabel}>DPS</span>
                      <span>{formatNumber(slot.totalDPS, 1)}</span>
                    </div>
                  </div>
                </div>
                {/* Show weapons mounted on turret */}
                {slot.weapons.length > 0 && (
                  <div className={styles.turretWeapons}>
                    {slot.weapons.map((weapon, idx) => {
                      const weaponKey = `${slot.hardpointId}-${weapon.loadedItemId}`;
                      const isExpanded = expandedTurretWeapon === weaponKey;
                      const sizeWeapons = availableWeapons.get(weapon.size) || [];
                      const isLoading = loadingWeapons.has(weapon.size);
                      
                      return (
                        <div key={weapon.loadedItemId}>
                          <div 
                            className={styles.turretWeapon}
                            onClick={() => !readOnly && toggleTurretWeaponDropdown(slot.hardpointId, weapon.loadedItemId, weapon.size)}
                          >
                            <span className={styles.turretWeaponSize}>S{weapon.size}</span>
                            <div className={styles.damageIndicator} style={{ backgroundColor: damageTypeColors[weapon.damageType] }} />
                            <span className={styles.turretWeaponName}>{weapon.displayName}</span>
                            <span className={styles.turretWeaponDPS}>{formatNumber(weapon.dps, 1)} DPS</span>
                            {!readOnly && <ChevronDown size={14} className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`} />}
                          </div>
                          {/* Turret weapon metadata and stats */}
                          <div className={styles.turretWeaponMeta} style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginLeft: '24px', marginTop: '4px' }}>
                            {weapon.manufacturer && (
                              <>
                                <span>{weapon.manufacturer}</span>
                                <span style={{ margin: '0 6px' }}>•</span>
                              </>
                            )}
                            <span>Grade {['A', 'B', 'C', 'D'][weapon.grade - 1] || '?'}</span>
                            {weapon.fireRate > 0 && (
                              <>
                                <span style={{ margin: '0 6px' }}>•</span>
                                <span>{formatNumber(weapon.fireRate, 0)} rpm</span>
                              </>
                            )}
                            {weapon.damagePerShot > 0 && (
                              <>
                                <span style={{ margin: '0 6px' }}>•</span>
                                <span>{formatNumber(weapon.damagePerShot, 1)} dmg</span>
                              </>
                            )}
                          </div>
                          
                          {/* Weapon Dropdown */}
                          {isExpanded && !readOnly && (
                            <div className={styles.dropdown}>
                              <div className={styles.dropdownHeader}>
                                Select S{weapon.size} Turret Weapon
                              </div>
                              {isLoading ? (
                                <div className={styles.loading}>Loading...</div>
                              ) : sizeWeapons.length === 0 ? (
                                <div className={styles.noOptions}>No weapons available</div>
                              ) : (
                                <div className={styles.optionsList}>
                                  {sizeWeapons.map(w => {
                                    const wDPS = calculateDPS(w.weaponStats?.fireRate || null, w.weaponStats?.damagePerShot || null);
                                    const wDamageType = getDamageType(w.weaponStats?.damageType, w.itemName);
                                    const isSelected = weapon.weaponName === w.itemName;
                                    
                                    return (
                                      <div
                                        key={w.id}
                                        className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTurretWeaponSelect(slot.hardpointId, weapon.loadedItemId, w);
                                        }}
                                      >
                                        <div className={styles.optionInfo}>
                                          <div className={styles.optionName}>{w.localizedName}</div>
                                          <div className={styles.optionMeta}>
                                            <span>{w.manufacturer}</span>
                                            <span style={{ margin: '0 4px' }}>•</span>
                                            <span>S{w.size}</span>
                                            <span style={{ margin: '0 4px' }}>•</span>
                                            <span>Grade {['A', 'B', 'C', 'D'][w.grade - 1] || '?'}</span>
                                          </div>
                                          <div className={styles.optionMeta} style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                                            {w.weaponStats?.fireRate && w.weaponStats.fireRate > 0 && (
                                              <span>{formatNumber(w.weaponStats.fireRate, 0)} rpm</span>
                                            )}
                                            {w.weaponStats?.damagePerShot && w.weaponStats.damagePerShot > 0 && (
                                              <>
                                                <span style={{ margin: '0 4px' }}>•</span>
                                                <span>{formatNumber(w.weaponStats.damagePerShot, 1)} dmg</span>
                                              </>
                                            )}
                                            {w.weaponStats?.range && w.weaponStats.range > 100 && (
                                              <>
                                                <span style={{ margin: '0 4px' }}>•</span>
                                                <span>{formatNumber(w.weaponStats.range / 1000, 1)}km</span>
                                              </>
                                            )}
                                            {w.weaponStats?.velocity && w.weaponStats.velocity > 0 && (
                                              <>
                                                <span style={{ margin: '0 4px' }}>•</span>
                                                <span>{formatNumber(w.weaponStats.velocity, 0)} m/s</span>
                                              </>
                                            )}
                                            {w.weaponStats?.ammoCapacity && w.weaponStats.ammoCapacity > 0 && (
                                              <>
                                                <span style={{ margin: '0 4px' }}>•</span>
                                                <span>{formatNumber(w.weaponStats.ammoCapacity, 0)} rounds</span>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                        <div className={styles.optionStats}>
                                          <div className={styles.damageIndicator} style={{ backgroundColor: damageTypeColors[wDamageType] }} />
                                          <span>{formatNumber(wDPS, 1)} DPS</span>
                                          {isSelected && <Check size={14} className={styles.checkIcon} />}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Missile Slots */}
      {missileSlots.length > 0 && (
        <>
          <div className={styles.sectionHeader}>
            <Rocket size={16} />
            <span>Missiles ({missileSlots.length})</span>
          </div>
          <div className={styles.weaponList}>
            {missileSlots.map(slot => {
              const isExpanded = expandedMissile === slot.hardpointId;
              const sizeMissiles = availableMissiles.get(slot.missileSize) || [];
              const isLoading = loadingMissiles.has(slot.missileSize);
              const canSwap = !readOnly;
              
              return (
                <div key={slot.hardpointId} className={styles.weaponItem}>
                  <div className={styles.weaponHeader}>
                    <div className={styles.weaponInfo}>
                      <div className={styles.sizeIndicator}>S{slot.hardpointSize}</div>
                      <div className={styles.weaponDetails}>
                        <div className={styles.weaponName}>{slot.rackDisplayName}</div>
                        <div className={styles.weaponMeta}>
                          <span>{slot.missileCount}x S{slot.missileSize} {slot.missileType}</span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.weaponStats}>
                      <div className={styles.statValue}>
                        <span className={styles.statLabel}>TOTAL DMG</span>
                        <span>{slot.totalDamage > 0 ? formatNumber(slot.totalDamage, 0) : '-'}</span>
                      </div>
                    </div>
                  </div>
                  {/* Show loaded missiles */}
                  <div className={styles.missileInfo}>
                    <div 
                      className={styles.turretWeapon}
                      onClick={() => !readOnly && toggleMissileDropdown(slot.hardpointId, slot.missileSize)}
                    >
                      <span className={styles.turretWeaponSize}>S{slot.missileSize}</span>
                      <div className={styles.damageIndicator} style={{ backgroundColor: '#ff6b6b' }} />
                      <span className={styles.turretWeaponName}>{slot.missileDisplayName}</span>
                      <span className={styles.turretWeaponDPS}>{formatNumber(slot.totalDamage / slot.missileCount, 0)} dmg</span>
                      {!readOnly && <ChevronDown size={14} className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`} />}
                    </div>
                    {/* Missile metadata and stats */}
                    <div className={styles.turretWeaponMeta} style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginLeft: '24px', marginTop: '4px' }}>
                      <span>{slot.manufacturer}</span>
                      <span style={{ margin: '0 6px' }}>•</span>
                      <span>Grade {['A', 'B', 'C', 'D'][slot.missileGrade - 1] || '?'}</span>
                      {slot.speed > 0 && (
                        <>
                          <span style={{ margin: '0 6px' }}>•</span>
                          <span>{formatNumber(slot.speed, 0)} m/s</span>
                        </>
                      )}
                      {slot.lockTime > 0 && (
                        <>
                          <span style={{ margin: '0 6px' }}>•</span>
                          <span>Lock: {slot.lockTime.toFixed(1)}s</span>
                        </>
                      )}
                      {slot.launchDelay > 0 && (
                        <>
                          <span style={{ margin: '0 6px' }}>•</span>
                          <span>Delay: {slot.launchDelay.toFixed(2)}s</span>
                        </>
                      )}
                      {slot.lockingRange > 0 && (
                        <>
                          <span style={{ margin: '0 6px' }}>•</span>
                          <span>Range: {formatNumber(slot.lockingRange / 1000, 1)}km</span>
                        </>
                      )}
                    </div>
                    
                    {/* Missile Dropdown */}
                    {isExpanded && !readOnly && (
                      <div className={styles.dropdown}>
                        <div className={styles.dropdownHeader}>
                          Select S{slot.missileSize} Missile
                        </div>
                        {isLoading ? (
                          <div className={styles.loading}>Loading...</div>
                        ) : sizeMissiles.length === 0 ? (
                          <div className={styles.noOptions}>No missiles available</div>
                        ) : (
                          <div className={styles.optionsList}>
                            {sizeMissiles.map(missile => {
                              const missileDamage = missile.missileStats?.damage || 0;
                              const totalNewDamage = missileDamage * slot.missileCount;
                              const isSelected = slot.missileName === missile.itemName;
                              
                              return (
                                <div
                                  key={missile.id}
                                  className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('🚀 Missile click:', { slotLoadedItemId: slot.loadedItemId, missileId: missile.id });
                                    if (slot.loadedItemId) {
                                      handleMissileSelect(slot.hardpointId, slot.loadedItemId, missile);
                                    } else {
                                      console.error('❌ No loadedItemId for missile slot');
                                    }
                                  }}
                                >
                                  <div className={styles.optionInfo}>
                                    <div className={styles.optionName}>{missile.localizedName || missile.itemName}</div>
                                    <div className={styles.optionMeta}>
                                      <span>{missile.manufacturer}</span>
                                      <span style={{ margin: '0 4px' }}>•</span>
                                      <span>S{missile.size}</span>
                                      <span style={{ margin: '0 4px' }}>•</span>
                                      <span>Grade {['A', 'B', 'C', 'D'][missile.grade - 1] || '?'}</span>
                                    </div>
                                    <div className={styles.optionMeta} style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                                      {missile.missileStats?.speed && missile.missileStats.speed > 0 && (
                                        <span>{formatNumber(missile.missileStats.speed, 0)} m/s</span>
                                      )}
                                      {missile.missileStats?.lockTime && missile.missileStats.lockTime > 0 && (
                                        <>
                                          <span style={{ margin: '0 4px' }}>•</span>
                                          <span>Lock: {missile.missileStats.lockTime.toFixed(1)}s</span>
                                        </>
                                      )}
                                      {missile.missileStats?.launchDelay && missile.missileStats.launchDelay > 0 && (
                                        <>
                                          <span style={{ margin: '0 4px' }}>•</span>
                                          <span>Delay: {missile.missileStats.launchDelay.toFixed(2)}s</span>
                                        </>
                                      )}
                                      {missile.missileStats?.lockingRange && missile.missileStats.lockingRange > 0 && (
                                        <>
                                          <span style={{ margin: '0 4px' }}>•</span>
                                          <span>Range: {formatNumber(missile.missileStats.lockingRange / 1000, 1)}km</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className={styles.optionStats}>
                                    <div className={styles.damageIndicator} style={{ backgroundColor: '#ff6b6b' }} />
                                    <span>{formatNumber(missileDamage, 0)} dmg</span>
                                    {isSelected && <Check size={14} className={styles.checkIcon} />}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default EmbeddedWeaponList;
